/**
 * A FELTÖLTÉSI KORLÁTOK — a szerver szabálya, egy helyen.
 *
 * A kanonikus forrás: `packages/upload-limits/upload-limits.ts`; a szervizekbe
 * onnan másolódik (`python3 scripts/shared-sync.py`), a `--check` módja pedig
 * a pre-commit hookban és a CI-ban is fut. OTT kell szerkeszteni, nem a
 * `src/contracts/generated/` alatti másolatban — ez a fejléc a másolatban is
 * ott áll, tehát nem mondhatja magára, hogy „ez a fájl a forrás".
 *
 * MIÉRT KELL EGYÁLTALÁN MEGOSZTANI. A szerver a KÉRÉST utasítja el, nem a
 * hibás fájlt: a méretkorlát túllépése 413, egyetlen rossz típus 415 —
 * mindkettő az EGÉSZ adagra. A kliens ezért előre részletekre bont és előre
 * szűr (`chunking.ts`), különben egy 30 fájlos ejtésből, amiben van egy
 * `.xlsx`, NULLA bizonylat érkezne be. Ez a tervezés viszont csak akkor jó,
 * ha a kliens UGYANAZOKAT a számokat ismeri.
 *
 * ⚠️ A DRIFT LEGROSSZABB IRÁNYA a típuslista BŐVÍTÉSE. Ha a szerver ezután
 * elfogad egy új formátumot, de a kliens listája nem követi, a kliens azokat
 * a fájlokat HELYBEN utasítja vissza, a szervert meg sem kérdezve — és egy
 * olyan mondattal, ami akkorra már hamis („Csak PDF-et és fényképet…").
 * Ezért van itt a lista, és nem három példányban.
 *
 * ⚠️ EZ NEM A KIKÉNYSZERÍTÉS HELYE. A korlát a szerveren él és ott is marad
 * (`document.controller.ts` → `UPLOAD_OPTIONS`); a felületek csak ELŐRE
 * LÁTNAK vele. A közös forrás azt garantálja, hogy ugyanazt lássák.
 */

/**
 * Amit elfogadunk. A `§8c` egy típusra szűkít (bejövő számla, PDF), de az
 * ügyfél elsődleges művelete a MOBIL FOTÓ — a makett első gombja
 * („Lefotózom") kamerát nyit. Egy csak-PDF korlát ezt a gombot hazuggá tenné.
 *
 * A LISTA ZÁRT, ÉS EZ SZÁNDÉKOS. A visszaolvasás `inline` fejléccel megy, hogy
 * a telefon meg tudja nyitni — egy `text/html` feltöltés így a mi originünkön
 * futó szkript lenne. A `2.6/9` (XXE) ugyanezt mondja ki a másik irányból: a
 * külső forrásból érkező tartalom formátumát nem hisszük el, hanem szűkítjük.
 *
 * ⚠️ AMIT ELFOGADUNK, AZT NEM FELTÉTLENÜL TUDJUK MEGMUTATNI. A HEIC/HEIF-et
 * a Safarin kívül egyik böngésző sem dekódolja — az admin beágyazó-döntése
 * ezért SZŰKEBB halmazon dolgozik (`original.ts` → `EMBEDDABLE_IMAGE_TYPES`).
 * A kettő két külön kérdés, és a különbségük csak akkor marad látható, ha
 * külön is kimondható.
 */
export const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/webp',
] as const;

/**
 * Elfogadjuk-e ezt a típust.
 *
 * A `Content-Type` az ÜGYFÉLTŐL jön, tehát nem bizonyíték: egy `.html` fájl
 * `image/jpeg`-ként bemondva átjut. Ez a szűrő a VÉLETLEN rossz feltöltést
 * fogja meg, és leszűkíti azt a halmazt, amit a `nosniff` mellett a böngésző
 * egyáltalán értelmezhet. A tartalom valódi vizsgálata (mágikus bájtok) az
 * OCR-rel együtt jön — addig a `nosniff` és a zárt lista a védelem.
 *
 * AZ ÜRES `type` IS ELBUKIK, és ez szándékos: a halmazban sincs benne, tehát
 * a 415 úgyis jönne. Jobb a kliensen megmondani, minek nevezzük, mint egy
 * egész köteget elbuktatni vele.
 */
const ACCEPTED_MIME_TYPE_SET: ReadonlySet<string> = new Set(ACCEPTED_MIME_TYPES);

export function isAcceptedMimeType(mimeType: string): boolean {
  return ACCEPTED_MIME_TYPE_SET.has(mimeType);
}

/** Az `<input accept="…">` értéke — ugyanaz a lista, vesszővel. */
export const ACCEPT_ATTRIBUTE = ACCEPTED_MIME_TYPES.join(',');

/**
 * HÁNY FÁJL MEHET EGY KÉRÉSBEN.
 *
 * A KORLÁT NEM EZ A SZÁM, HANEM A 25 MiB. A `feltoltesi-korlatok-javaslat.md`
 * 1. rétege kimondja: a kérésenkénti méret azért 25 MiB, mert a multer a hash
 * miatt MEMÓRIÁBA pufferel (`2.6/20`). A több fájl ezt a keretet nem
 * szorozhatja — különben egy húszfájlos kéréssel 500 MiB-ot kérnénk a
 * folyamattól, és a döntés indoklása veszne el, nem csak a száma.
 *
 * Ezért a darabszám bőkezű, az ÖSSZMÉRET viszont ugyanaz a 25 MiB. Ugyanennek
 * a doksinak a mérése szerint az átlagos bizonylat 300 KB (szkennelt) és
 * 1,2 MB (telefonos fotó) közt van — 25 MiB-ba tehát nagyságrendileg ennyi
 * fér, és a `csoportos` mód pont ezekre való.
 *
 * ⚠️ AMI EZZEL NEM OLDÓDIK MEG: egy hazudó `Content-Length` mellett a multer
 * fájlonként még mindig 25 MiB-ig pufferel, tehát a legrosszabb eset
 * `darabszám × 25 MiB`. Ez ellen a doksi 2. rétege (`@nestjs/throttler`,
 * felhasználóra) és a `StorageProvider` stream-re váltása véd — egyik sem ez
 * a korlát.
 */
export const MAX_FILES_PER_UPLOAD = 20;

/**
 * 25 MiB, A KÉRÉSRE — nem fájlonként. Egy telefonos fotó 3–8 MB, egy
 * többoldalas szkennelt PDF ritkán megy 20 fölé.
 *
 * A KORLÁT A MEMÓRIA MIATT VAN, nem a tárhely miatt. A tartalom-alapú kulcs a
 * teljes bájtsorozatból képződik (`2.6/20`), tehát a fájl a hash kiszámításáig
 * a memóriában van. Amikor ez kevés lesz, a `StorageProvider.put` vált
 * stream-re — a szerződése erről külön beszél.
 *
 * ⚠️ ÜZEMELTETÉS: EGY ÉL-PROXY A TELJES TÖRZSET MÉRI, mi csak a fájlok
 * összegét — a multipart-többletet nem látjuk. Ha proxy áll az API előtt, a
 * korlátjának ezt a 25 MiB-ot PLUSZ a többletet kell clearelnie. Az nginx
 * alapértelmezése 1 MiB, és akkor minden teli részlet 413-mal jön vissza,
 * gyakran ÜRES TÖRZZSEL — a felületnek ezért van saját mondata a 413-ra.
 */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
